import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { graphql } from '@/shared/graphql/router'
import { routerApolloClient } from '@/shared/graphql/client'

export enum FeatureFlag {
  AI = 'ai',
  Origin = 'origin',
}

const FEATURE_FLAGS_QUERY = graphql(`
  query FeatureFlags {
    featureFlags {
      enabled
    }
  }
`)

const RESOLVE_TIMEOUT_MS = 4000

export const useFeatureFlagStore = defineStore('featureFlags', () => {
  const flagsLoaded = ref(false)
  const flags = ref<Record<string, boolean>>({})

  let resolveFlags: (() => void) | null = null
  const flagsReadyPromise = new Promise<void>((resolve) => {
    resolveFlags = resolve
  })

  const settle = (enabled: string[]) => {
    const updated: Record<string, boolean> = {}
    for (const flag of enabled) {
      updated[flag] = true
    }
    flags.value = updated
    flagsLoaded.value = true
    resolveFlags?.()
  }

  let started = false

  const init = () => {
    if (started) return
    started = true

    const timeout = setTimeout(() => resolveFlags?.(), RESOLVE_TIMEOUT_MS)

    routerApolloClient
      .query({ query: FEATURE_FLAGS_QUERY, fetchPolicy: 'network-only' })
      .then(({ data }) => settle(data.featureFlags.enabled))
      .catch(() => settle([]))
      .finally(() => clearTimeout(timeout))
  }

  const isEnabled = (flag: FeatureFlag): boolean => {
    if (!flagsLoaded.value) return false
    return !!flags.value[flag]
  }

  const useFlag = (flag: FeatureFlag) => {
    return computed(() => isEnabled(flag))
  }

  const waitForFlags = (): Promise<void> => flagsReadyPromise

  return {
    flagsLoaded,
    init,
    isEnabled,
    useFlag,
    waitForFlags,
  }
})
